import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../lib/axios';
import { Users, Globe, Store, Search, FileText, TrendingUp, Loader2 } from 'lucide-react';

interface AdminStats {
  total_users: number;
  active_users: number;
  verified_users: number;
  plan_distribution: Record<string, number>;
  provider_distribution: Record<string, number>;
  new_users_30d: number;
  active_subscriptions: number;
}

interface DomainStats {
  total_domains: number;
  total_value: number;
  average_price: number;
  total_sellers: number;
  total_listings: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState<AdminStats | null>(null);
  const [domainStats, setDomainStats] = useState<DomainStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [usRes, dsRes] = await Promise.all([
          api.get('/api/admin/stats'),
          api.get('/api/domains/stats'),
        ]);
        setUserStats(usRes.data);
        setDomainStats(dsRes.data);
      } catch (err: any) {
        if (err.response?.status === 403) navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [navigate]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
        </div>
      </DashboardLayout>
    );
  }

  const formatNum = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const topCards = [
    { label: 'Total Domains', value: formatNum(domainStats?.total_domains ?? 0), icon: Globe, color: 'bg-blue-500' },
    { label: 'Total Sellers', value: formatNum(domainStats?.total_sellers ?? 0), icon: Store, color: 'bg-green-500' },
    { label: 'Price Listings', value: formatNum(domainStats?.total_listings ?? 0), icon: FileText, color: 'bg-purple-500' },
    { label: 'Avg Price', value: `$${domainStats?.average_price?.toFixed(0) ?? '0'}`, icon: TrendingUp, color: 'bg-orange-500' },
    { label: 'Total Users', value: formatNum(userStats?.total_users ?? 0), icon: Users, color: 'bg-indigo-500' },
    { label: 'Paid Subs', value: formatNum(userStats?.active_subscriptions ?? 0), icon: Search, color: 'bg-pink-500' },
  ];

  const adminLinks = [
    { label: 'Manage Domains', desc: 'Browse, import, edit the domain database', href: '/admin/domains', icon: Globe },
    { label: 'Manage Sellers', desc: 'Add, edit, verify seller marketplaces', href: '/admin/sellers', icon: Store },
    { label: 'Submissions', desc: 'Review "Add your websites" requests', href: '/admin/submissions', icon: FileText },
    { label: 'Users', desc: 'Manage user accounts and plans', href: '/admin/users', icon: Users },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Platform overview and management</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {topCards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className={`${card.color} p-2 rounded-lg w-fit mb-3`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
              </div>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {adminLinks.map(link => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <Icon className="w-6 h-6 text-brand-500 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{link.label}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{link.desc}</p>
              </Link>
            );
          })}
        </div>

        {/* Distribution Charts */}
        {userStats && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Plan Distribution</h2>
              <div className="space-y-3">
                {Object.entries(userStats.plan_distribution).map(([plan, count]) => {
                  const pct = userStats.total_users > 0 ? Math.round((count / userStats.total_users) * 100) : 0;
                  return (
                    <div key={plan}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{plan}</span>
                        <span className="text-sm text-gray-500">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                        <div className="h-2 bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Auth Providers</h2>
              <div className="space-y-3">
                {Object.entries(userStats.provider_distribution).map(([provider, count]) => {
                  const pct = userStats.total_users > 0 ? Math.round((count / userStats.total_users) * 100) : 0;
                  const colors: Record<string, string> = { local: 'bg-gray-500', google: 'bg-red-500', github: 'bg-gray-800 dark:bg-gray-400' };
                  return (
                    <div key={provider}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{provider}</span>
                        <span className="text-sm text-gray-500">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                        <div className={`h-2 ${colors[provider] || 'bg-brand-500'} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
